import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: "page-wrapper",
  id: "assessment-question-main-content",
  tabindex: "-1"
}
const _hoisted_2 = { class: "assessment-wrapper" }
const _hoisted_3 = {
  key: 0,
  role: "heading",
  id: "assessment-title",
  tabindex: "0",
  "aria-level": "1",
  class: "page-title",
  "data-test": "title"
}
const _hoisted_4 = {
  id: "question-wizard",
  "data-test": "question-wizard"
}

import { IonPage, IonContent, IonModal, IonProgressBar } from "@ionic/vue";
import { useAssessmentStore, UserQuestionResponse } from "@/store";
import PageBanner from "@/components/Common/PageBanner/PageBanner.vue";
import Breadcrumbs from "@/components/Common/Breadcrumbs/Breadcrumbs.vue";
import QuestionWizard from "@/components/Assessment/QuestionWizard/QuestionWizard.vue";
import { computed, ref, onMounted, onUpdated, nextTick } from "vue";
import { useRouter, onBeforeRouteLeave } from "vue-router";
import { parseImageUrl } from "@/helpers";
import debounce from "lodash/debounce";


export default /*@__PURE__*/_defineComponent({
  __name: 'AssessmentQuestion',
  setup(__props) {

const displaySavingInProgressModal = ref(false);
const assessmentStore = useAssessmentStore();
const router = useRouter();

const assessmentItemIndex = computed<number>(() => {
  return assessmentStore.getCurrentItemIndex || 0;
});

const savedResponses = computed<Array<number>>(() => {
  return assessmentStore.getCurrentSavedResponses;
});

const isNextLoading = ref(false);
const isPrevLoading = ref(false);

const bannerImage = ref(require("@/assets/course-thumbnail-placeholder.png"));

onMounted(async () => {
  if (assessment.value.featureImage) {
    bannerImage.value = parseImageUrl(
      assessment?.value.featureImage,
      1000,
      2500
    );
  }
});

onUpdated(async () => {
  const { isAssessmentDetailLoaded, isAssessmentStarted } = assessmentStore;
  const { fullPath } = router.currentRoute.value;
  if (
    (!isAssessmentDetailLoaded || !isAssessmentStarted) &&
    fullPath?.startsWith("/tabs/tools/") &&
    fullPath?.endsWith("/questions")
  ) {
    // Assessment is not loaded navigate to tools home page
    router.push({ name: "Tools", replace: true });
    return;
  }

  await focusOnTitle();
});

onBeforeRouteLeave((to, from, next) => {
  const assessmentStore = useAssessmentStore();
  assessmentStore.initialiseAssessment();
  next();
});

const assessment = computed(() => {
  return assessmentStore.getAssessmentDetail ?? null;
});

const assessmentItem = computed(() => {
  const items = assessment.value?.items;
  if (
    !items ||
    assessmentItemIndex.value < 0 ||
    assessmentItemIndex.value >= items.length
  ) {
    return undefined;
  }
  return items[assessmentItemIndex.value];
});

const savedResponse = computed(() => {
  if (
    assessmentItem.value &&
    assessmentItemIndex.value >= 0 &&
    assessmentItemIndex.value <= savedResponses.value.length - 1
  ) {
    return savedResponses.value[assessmentItemIndex.value];
  }
  return undefined;
});

const questionCount = computed(() => {
  return assessment.value.items?.length || 0;
});

const saveResponse = (event: UserQuestionResponse): void => {
  assessmentStore.saveUserQuestionResponse(event);
};

const startSavingInProgressTimer = debounce(() => {
  displaySavingInProgressModal.value = true;
}, 5000);

const cancelSavingInProgressTimer = () => {
  startSavingInProgressTimer.cancel();
  displaySavingInProgressModal.value = false;
};

const nextQuestion = async (): Promise<void> => {
  startSavingInProgressTimer();
  if (assessmentItemIndex.value === assessment.value.items.length - 1) {
    completeAssessment();
    return;
  }
  try {
    isNextLoading.value = true;
    await assessmentStore.saveItemAndContinue();
    assessmentStore.setCurrentItem(assessmentItemIndex.value + 1);
  } finally {
    cancelSavingInProgressTimer();
    isNextLoading.value = false;
    await focusOnTitle();
  }
};

const completeAssessment = async () => {
  try {
    isNextLoading.value = true;
    await assessmentStore.completeAssessment();
    if (assessmentStore.isAssessmentCompleted) {
      const assessmentId = assessment.value.baseId;
      resetQuiz();
      router.push({
        name: "AssessmentDetail",
        params: { id: assessmentId },
      });
    }
  } finally {
    cancelSavingInProgressTimer();
    isNextLoading.value = false;
  }
};

const previousQuestion = async (): Promise<void> => {
  if (assessmentItemIndex.value > 0) {
    try {
      isPrevLoading.value = true;
      startSavingInProgressTimer();
      await assessmentStore.saveItemAndContinue();
      assessmentStore.setCurrentItem(assessmentItemIndex.value - 1);
    } finally {
      cancelSavingInProgressTimer();
      isPrevLoading.value = false;
      await focusOnTitle();
    }
  }
};

const focusOnTitle = async (): Promise<void> => {
  await nextTick(() => {
    const title = document.getElementById("assessment-title");
    if (title) {
      title.focus();
    }
  });
};

const resetQuiz = (): void => {
  assessmentStore.initialiseAssessment();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), null, {
    default: _withCtx(() => [
      _createVNode(_unref(IonContent), {
        "data-test": "assessment-question",
        class: "page-wrapper"
      }, {
        default: _withCtx(() => [
          _createVNode(PageBanner, {
            imagePath: bannerImage.value,
            "data-test": "banner-image"
          }, null, 8, ["imagePath"]),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(Breadcrumbs, {
              tab: "tools",
              parentType: "quiz",
              parent: assessment.value.baseId,
              parentTitle: assessment.value?.title,
              doc: "Quiz",
              "data-test": "breadcrumbs"
            }, null, 8, ["parent", "parentTitle"]),
            _createElementVNode("div", _hoisted_2, [
              (assessment.value?.title)
                ? (_openBlock(), _createElementBlock("h1", _hoisted_3, _toDisplayString(assessment.value?.title), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_4, [
                (assessmentItem.value)
                  ? (_openBlock(), _createBlock(QuestionWizard, {
                      assessmentItem: assessmentItem.value,
                      questionIndex: assessmentItemIndex.value + 1,
                      questionCount: questionCount.value,
                      previousResponse: savedResponse.value,
                      onPrevClick: _cache[0] || (_cache[0] = ($event: any) => (previousQuestion())),
                      onNextClick: _cache[1] || (_cache[1] = ($event: any) => (nextQuestion())),
                      onSaveResponse: _cache[2] || (_cache[2] = ($event: any) => (saveResponse($event))),
                      key: assessmentItemIndex.value + 1,
                      isNextLoading: isNextLoading.value,
                      isPrevLoading: isPrevLoading.value
                    }, null, 8, ["assessmentItem", "questionIndex", "questionCount", "previousResponse", "isNextLoading", "isPrevLoading"]))
                  : _createCommentVNode("", true)
              ])
            ])
          ]),
          _createVNode(_unref(IonModal), {
            id: "saving-in-progress-modal",
            "is-open": displaySavingInProgressModal.value
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonContent), { class: "ion-padding" }, {
                default: _withCtx(() => [
                  _createElementVNode("h1", null, _toDisplayString(_ctx.$t("assessmentSavingInProgressModal.title")), 1),
                  _createVNode(_unref(IonProgressBar), { type: "indeterminate" })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["is-open"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})